import {
  ComponentProps,
  FC,
  PropsWithChildren,
  useLayoutEffect,
  useState,
} from "react";
import { Outlet } from "react-router-dom";
import { Button } from "react-daisyui";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

import Portal from "@/components/shared/Portal/Portal.tsx";
import { cn } from "@/utils/classNames.ts";
import {
  TripLayoutProvider,
  useTripLayoutContext,
} from "@/layouts/TripLayout/TripLayoutProvider.tsx";
import { PropsWithClassName } from "@/types/PropsWithClassName";
import Navbar from "@/layouts/TripLayout/Navbar.tsx";

type LayoutProps = {
  mainClassName?: string;
};

const Layout: FC<LayoutProps> = (props) => {
  return (
    <TripLayoutProvider>
      <LayoutInner {...props} />
    </TripLayoutProvider>
  );
};

const LayoutInner: FC<LayoutProps> = ({ mainClassName }) => {
  const { containerElementRef, isMenuOpen, setIsMenuOpen } =
    useTripLayoutContext();

  return (
    <>
      <main
        ref={containerElementRef}
        id="trip-layout-content-container"
        className={cn(
          "relative z-0 flex min-h-[100dvh] w-full grow flex-col bg-journi-theme-light",
          mainClassName,
        )}
      >
        <Outlet />
      </main>

      <div className="drawer z-50">
        <input
          id="trip-navigation-drawer"
          type="checkbox"
          className="drawer-toggle"
          checked={isMenuOpen}
          onChange={(event) => setIsMenuOpen(event.target.checked)}
          aria-label="Navigation menu"
        />

        <div className="drawer-content" />

        <div className="drawer-side">
          <label
            htmlFor="trip-navigation-drawer"
            className="drawer-overlay"
            aria-label="Close navigation menu"
          />

          <NavigationPanel />
        </div>
      </div>

      <LayoutDrawer />
    </>
  );
};

const InnerDrawer: FC<PropsWithChildren> = ({ children }) => {
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((previous) => !previous);
  }, []);

  return (
    <Portal watch={watch} elementId="layout-drawer">
      {children}
    </Portal>
  );
};

const OpenMenuButton: FC<PropsWithClassName<ComponentProps<typeof Button>>> = ({
  className,
  ...props
}) => {
  const { isMenuOpen, setIsMenuOpen } = useTripLayoutContext();

  return (
    <Button
      {...props}
      shape="circle"
      aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
      title={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
      onClick={() => setIsMenuOpen((previous) => !previous)}
      className={cn("h-[38px] min-h-[38px] w-[38px] p-2", className)}
    >
      {isMenuOpen
        ? <XMarkIcon aria-hidden="true" />
        : <Bars3Icon aria-hidden="true" />}
    </Button>
  );
};

const NavigationPanel: FC<PropsWithClassName> = ({ className }) => {
  return (
    <aside
      className={cn(
        "flex h-[100dvh] w-[260px] max-w-[88vw] flex-col overflow-hidden border-r border-[#80691F]/60 bg-[#F3E8CA] shadow-2xl",
        className,
      )}
    >
      <header className="flex shrink-0 items-center justify-between border-b border-[#80691F]/50 px-5 py-3">
        <span className="font-feature text-2xl font-semibold tracking-wide text-base-content">
          JOURNI
        </span>

        <OpenMenuButton
          color="ghost"
          className="border-2 border-[#80691F] text-[#80691F] hover:bg-white/35"
        />
      </header>

      <div className="min-h-0 flex-1 px-4 pb-3 pt-2">
        <Navbar className="h-full min-h-0 w-full" />
      </div>
    </aside>
  );
};

const OpenDrawerButton: FC<
  PropsWithClassName<ComponentProps<typeof Button>>
> = ({ className, ...props }) => {
  const { setIsDrawerOpen } = useTripLayoutContext();

  return (
    <Button
      {...props}
      shape="circle"
      aria-label="Toggle drawer"
      title="Toggle drawer"
      onClick={() => setIsDrawerOpen((previous) => !previous)}
      className={cn("h-[34px] min-h-[34px] w-[34px] p-1.5", className)}
    />
  );
};

const LayoutDrawer: FC = () => {
  const { isDrawerOpen, setIsDrawerOpen } = useTripLayoutContext();

  return (
    <div className="drawer drawer-end z-50">
      <input
        id="layout-content-drawer"
        type="checkbox"
        className="drawer-toggle"
        checked={isDrawerOpen}
        onChange={(event) => setIsDrawerOpen(event.target.checked)}
        aria-label="Layout drawer"
      />

      <div className="drawer-content" />

      <div className="drawer-side">
        <label
          htmlFor="layout-content-drawer"
          className="drawer-overlay"
          aria-label="Close layout drawer"
        />

        <div className="flex h-[100dvh] max-h-[100dvh] max-w-[100vw] flex-col overflow-auto bg-journi-theme-light">
          <div
            id="layout-drawer"
            className="empty:hidden flex max-w-[100vw] grow"
          />
        </div>
      </div>
    </div>
  );
};

export default {
  Layout,
  Drawer: InnerDrawer,
  NavigationPanel,
  OpenMenuButton,
  OpenDrawerButton,
};
